import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FloatingShapes from '@/components/FloatingShapes';

export default function AboutHeroSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative flex items-center overflow-hidden bg-background-50"
    >
      {/* Animated floating shapes background */}
      <FloatingShapes count={20} color="accent" speed={0.5} />
      <div className="absolute inset-0 pointer-events-none">
        <FloatingShapes count={12} color="secondary" speed={0.5} />
      </div>

      {/* Background dot grid */}
      <div className="absolute inset-0 opacity-[0.04]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, oklch(var(--primary-500)) 1px, transparent 0)`,
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      {/* Decorative gradient orbs */}
      <div className="absolute top-10 right-20 w-96 h-96 bg-accent-500/8 rounded-full blur-3xl animate-[morphPulse_6s_ease-in-out_infinite]" />
      <div className="absolute bottom-10 left-20 w-[480px] h-[480px] bg-secondary-500/8 rounded-full blur-3xl animate-[morphPulse_8s_ease-in-out_infinite_1s]" />
      <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-accent-500/5 rounded-full blur-3xl animate-[morphFloat_7s_ease-in-out_infinite]" />

      <div className="relative w-full px-4 md:px-8 lg:px-12 pt-36 md:pt-44 pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto">
          <div
            className={`transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-accent-100/80 rounded-full w-fit mb-6">
              <span className="w-1.5 h-1.5 bg-accent-500 rounded-full animate-pulse" />
              <span className="text-xs font-mono font-medium text-accent-700 uppercase tracking-wider">
                {t('about.hero.badge')}
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-heading font-semibold text-foreground-950 leading-[1.1] tracking-tight mb-6 max-w-4xl">
              {t('about.hero.title1')}{' '}
              <span className="text-accent-500">{t('about.hero.title2')}</span>,{' '}
              {t('about.hero.title3')}
            </h1>

            {/* Subtitle */}
            <p className="text-base md:text-lg text-foreground-700 leading-relaxed max-w-2xl mb-8">
              {t('about.hero.subtitle')}
            </p>

            {/* Supporting statement */}
            <p className="text-sm text-foreground-600 leading-relaxed max-w-lg mb-10 border-l-2 border-accent-400 pl-4">
              {t('about.hero.support')}
            </p>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link
                to="/shape-your-project"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-md hover:bg-primary-600 transition-all hover:scale-[1.02] whitespace-nowrap"
              >
                {t('cta.shapeYourProject')}
                <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to="/how-we-work"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-secondary-100 text-secondary-900 border border-secondary-200 text-sm font-semibold rounded-md hover:bg-secondary-200 transition-all hover:scale-[1.02] whitespace-nowrap"
              >
                {t('about.hero.howWeWork')}
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}